import React from "react";
import TowerSection from "../TowerSection";
import {
  alertBellLevel,
  AlertBellLevel,
  DriverInactivityAlert,
  driverInactivityAlert,
  driverStateDisplay,
} from "../model/driverActivity";
import { DriverOpenOptions, followUpPill, noDeliveriesHelp, noDeliveriesLabel } from "../model/driverFocus";
import { activateOnKey } from "../model/towerShared";
import { formatMinutes } from "../model/towerFormat";
import type { TowerDriver, TowerDriverDetail } from "../../../types/controlTower";

const BELL_LABELS: Record<AlertBellLevel, string> = {
  warning: "Alerta leve: lleva un rato sin registrar entregas",
  critical: "Alerta crítica: lleva mucho tiempo sin registrar entregas",
  muted: "Sin entregas hoy: todavía no hay una entrega para medir la inactividad",
};

function AlertBell({ level }: { level: AlertBellLevel }) {
  return (
    <span className={`tower-alert-bell ${level}`} role="img" aria-label={BELL_LABELS[level]} title={BELL_LABELS[level]}>
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
    </span>
  );
}

// Al lado de un número: cuántos más arrastra de días anteriores (driver.carried),
// que no se suman a lo de hoy. Chico y en gris para no desalinear la fila; al
// tocarlo abre el chofer con esos mismos envíos.
function PreviousNote({
  count,
  onOpen,
  kind = "inTransit",
}: {
  count: number;
  onOpen: () => void;
  // "all": todos los de días anteriores (columna Envíos); "inTransit": los que siguen en camino.
  kind?: "all" | "inTransit";
}) {
  if (!count) return null;
  const what = kind === "all" ? "con el chofer" : "en camino";
  return (
    <button
      type="button"
      className="tower-previous-note"
      title={`${count} más ${what} desde días anteriores, sin movimiento hoy. No se suman a lo de hoy.`}
      aria-label={`${count} ${what} de días anteriores`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      // Con teclado, Enter o espacio activan este botón y no la fila del chofer.
      onKeyDown={(event) => event.stopPropagation()}
    >
      +{count}
    </button>
  );
}

// Debajo del nombre del chofer: solo el detalle de la alerta. Cobros, cambios,
// retiros y lo reprogramado por Meli se ven al abrir el chofer.
function DriverNotes({ alert }: { alert: DriverInactivityAlert }) {
  if (!alert) return null;
  return (
    <small
      className={`tower-driver-note ${alert.level}`}
      title={alert.level === "noDeliveries" ? noDeliveriesHelp(alert.movedToday) : undefined}
    >
      {alert.level === "noDeliveries"
        ? noDeliveriesLabel(alert.movedToday)
        : `${formatMinutes(alert.idleMinutes)} sin entregar`}
    </small>
  );
}

// Una fila: el chofer en la zona (counts de hoy, carried de días anteriores).
function DriverRow({
  driver,
  zone,
  detail,
  overall,
  alertsEnabled,
  nowMs,
  onSelectDriver,
}: {
  driver: TowerDriver;
  zone: string;
  detail: TowerDriverDetail | undefined;
  // El chofer completo: la inactividad se mide con todas sus entregas, no solo las de la zona.
  overall: TowerDriver;
  alertsEnabled: boolean;
  nowMs: number;
  onSelectDriver: (driverId: string, options?: DriverOpenOptions) => void;
}) {
  const alert = alertsEnabled ? driverInactivityAlert(driver, overall, nowMs) : null;
  const bell = alertBellLevel(alert);
  const pill = followUpPill(alert, driverStateDisplay(driver.state));
  const openDriver = () => onSelectDriver(driver.id);
  const openPrevious = (universe?: "FLEX" | "NON_FLEX") =>
    onSelectDriver(driver.id, { status: "inTransit", mapStatus: "inTransit", zone, universe });
  const openAllPrevious = () => onSelectDriver(driver.id, { status: "all", mapStatus: "all", zone });
  return (
    <tr
      className={`tower-driver-row${driver.counts.total === 0 ? " tower-driver-row-previous" : ""}`}
      tabIndex={0}
      aria-label={`Abrir envíos de ${driver.name}`}
      onClick={openDriver}
      onKeyDown={activateOnKey(openDriver)}
    >
      <td>
        <span className="tower-driver-name">
          <button type="button" className="tower-driver-link" onClick={openDriver}>
            {driver.name}
          </button>
          {bell && <AlertBell level={bell} />}
        </span>
        <DriverNotes alert={alert} />
        {driver.kind !== "driver" && <small>Fuera del conteo de choferes</small>}
        {(detail?.zones.length || 0) > 1 && <small>Opera en {detail?.zones.length} zonas</small>}
      </td>
      <td>
        <span className="tower-cell-inline">
          {driver.counts.total}
          <PreviousNote count={driver.carried.total} onOpen={openAllPrevious} kind="all" />
        </span>
      </td>
      <td className="tower-green">{driver.counts.delivered}</td>
      <td>
        <span className="tower-cell-inline">
          <span className="tower-number-flex">{driver.counts.flexInTransit}</span>
          <PreviousNote count={driver.carried.flexInTransit} onOpen={() => openPrevious("FLEX")} />
        </span>
      </td>
      <td>
        <span className="tower-cell-inline">
          <span className="tower-number-nonflex">{driver.counts.nonFlexInTransit}</span>
          <PreviousNote count={driver.carried.nonFlexInTransit} onOpen={() => openPrevious("NON_FLEX")} />
        </span>
      </td>
      {/* En camino y avance en una sola celda, donde estaba Avance: el número arriba y la barra abajo. */}
      <td>
        <div className="tower-transit-progress">
          <span className="tower-cell-inline">
            <b>{driver.counts.inTransit}</b> en camino
            <PreviousNote count={driver.carried.inTransit} onOpen={() => openPrevious()} />
          </span>
          <div className="tower-progress">
            <progress max={100} value={driver.progress} aria-label={`Avance de ${driver.name} en ${zone}`} />
            <small>{driver.progress}%</small>
          </div>
        </div>
      </td>
      <td>{driver.lastDelivery ? driver.lastDelivery.slice(11) || "Sin hora" : "—"}</td>
      <td>
        <span className={`tower-state ${pill.tone}`}>{pill.label}</span>
      </td>
    </tr>
  );
}

// Una zona de Monitoreo: encabezado con activos y choferes, y la tabla.
export default function TowerZoneTable({
  zone,
  rows,
  shown,
  hasDrivers,
  filtering,
  activeKind = "all",
  open,
  onToggle,
  countLabel,
  scopeDescription,
  detailsById,
  alertsEnabled,
  nowMs,
  onSelectDriver,
}: {
  zone: string;
  // Todos los choferes de la zona y los que pasan el filtro, ya ordenados.
  rows: TowerDriver[];
  shown: TowerDriver[];
  hasDrivers: boolean;
  filtering: boolean;
  // Qué cuenta como "activo" en el encabezado: sigue al filtro de Seguimiento.
  activeKind?: "all" | "flex" | "nonFlex";
  open: boolean;
  onToggle: (open: boolean) => void;
  countLabel: string;
  scopeDescription: string;
  detailsById: Map<string, TowerDriverDetail>;
  alertsEnabled: boolean;
  nowMs: number;
  onSelectDriver: (driverId: string, options?: DriverOpenOptions) => void;
}) {
  const zoneDrivers = rows.filter((driver) => driver.kind === "driver");
  const shownDrivers = shown.filter((driver) => driver.kind === "driver");
  // "Activos": con algo en camino hoy, o con Flex / particulares en camino si ese es el filtro.
  const isActive = (driver: TowerDriver) =>
    activeKind === "flex"
      ? driver.counts.flexInTransit > 0
      : activeKind === "nonFlex"
        ? driver.counts.nonFlexInTransit > 0
        : driver.counts.inTransit > 0;
  const activeLabel =
    activeKind === "flex" ? "con Flex en camino" : activeKind === "nonFlex" ? "con particulares en camino" : "activos";
  return (
    <TowerSection
      className={`tower-zone-${zone.toLowerCase()}`}
      open={open}
      onToggle={onToggle}
      title={`Zona ${zone}`}
      help="Conteos de toda la zona; un chofer puede operar en varias"
      metrics={
        <>
          <span className="tower-active-drivers">
            <i aria-hidden="true" />
            {zoneDrivers.filter(isActive).length} {activeLabel}
          </span>
          <span>
            {shownDrivers.length}
            {filtering ? ` de ${zoneDrivers.length}` : ""} choferes
          </span>
        </>
      }
    >
      <div className="tower-table-scroll">
        <table className="tower-table tower-table-centered">
          <thead>
            <tr>
              <th>Chofer actual</th>
              <th title={scopeDescription || undefined}>{countLabel}</th>
              <th>Entregados</th>
              <th>Flex en camino</th>
              <th>Particulares en camino</th>
              <th>En camino · avance</th>
              <th>Última entrega</th>
              <th>Seguimiento</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((driver) => (
              <DriverRow
                key={driver.id}
                driver={driver}
                zone={zone}
                detail={detailsById.get(driver.id)}
                overall={detailsById.get(driver.id) ?? driver}
                alertsEnabled={alertsEnabled}
                nowMs={nowMs}
                onSelectDriver={onSelectDriver}
              />
            ))}
            {shown.length === 0 && (
              <tr>
                <td colSpan={8} className="tower-empty">
                  {hasDrivers
                    ? "No hay choferes que coincidan con estos filtros."
                    : "No hay envíos de esta zona en el corte seleccionado."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </TowerSection>
  );
}
